import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import { Option } from '../constants/telosair-interfaces';

type RadioButtonGroupProps<T> = {
  idName: string;
  options: Option<T>[];
  label?: string;
  spacing?: string; // Add spacing prop
  avoidMarginTop?: boolean;
  direction?: 'row' | 'col';
  disabled?: boolean;
};

/**
 * A component that represents a radio button group with options.
 * @param props - The component's props.
 * @param props.idName - Name of the radio button group.
 * @param props.options - The array of options to be displayed.
 * @param props.avoidMarginTop - Whether to avoid margin from top.
 * @param [props.label] - The label for the radio button group.
 * @param [props.spacing] - The spacing between options. Default is 'mr-4'.
 * @param props.direction - Direction of the buttons.
 * @param props.disabled - Whether to disable the radio button group.
 * @returns A React element representing the radio button group.
 */
export default function CustomRadioButtonGroup<T extends string | number>({
  idName,
  options,
  label,
  avoidMarginTop,
  direction = 'row',
  spacing = 'mr-4',
  disabled = false
}: RadioButtonGroupProps<T>): ReactElement {
  const { register, watch } = useFormContext();
  const checked = watch(idName);

  return (
    <div className={`relative ${!avoidMarginTop && 'mt-4'}`}>
      {label && (
        <div className="block text-gray-500 dark:text-gray-500 text-base font-medium">{label}</div>
      )}

      <div
        className={`flex text-base font-normal text-gray-300 dark:text-gray-300 flex-wrap ${
          direction === 'col' ? 'flex-col' : 'flex-row'
        }`}
      >
        {options.map((option) => (
          <label
            htmlFor={`radio-btn-${option.id}`}
            key={option.id}
            className={`inline-flex cursor-pointer items-center mt-2 ${spacing}`}
          >
            <input
              disabled={disabled}
              type="radio"
              id={`radio-btn-${option.id}`}
              value={option.id}
              checked={checked === option.id}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register(idName)}
              className="form-radio text-primary-600 h-5 w-5 bg-container-500 mr-2 cursor-pointer"
            />
            {option.name}
          </label>
        ))}
      </div>
    </div>
  );
}
